import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, NavigationExtras } from '@angular/router';
import { AuthService } from './auth.service';
import { TranslateService } from '@ngx-translate/core';
import { GlobalVariables } from '../../common/global-variables';
import { AUTH_CONSTANTS } from './auth-constants';
import {environment} from '../../environments/environment';

const _ = require('lodash');

@Component({
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
    public username: string;
    public password: string;
    public wrongUsernameOrPassword: boolean = false;
    public loading: boolean = false;

    public showGovCloudModal = false;

    private cloudType: string = '';
    private authConstants = AUTH_CONSTANTS;
    private isUnauthorized: boolean = false;
    private userNamePlaceholder: string = 'Username'
    loadedInVCRemotePlugin = false;
    remotePluginAuthErrors = [];
    loginErrorMessage: string = '';

    private authChangeEventSubscription = null;

    constructor(public authService: AuthService, public router: Router, public translate: TranslateService) {
        translate.use(GlobalVariables.LOCALE);
        this.authChangeEventSubscription = this.authService.getAuthChangeEvent().subscribe((evt) => {
            if (evt === 'LOGIN_COMPLETE') {
                this.onLoginComplete();
            } else if (evt === 'SHOW_GOVCLOUD_DISCLAIMER') {
                // show modal
                this.showGovCloudModal = true;
            }
        });
    }

    ngOnInit() {
        this.loadedInVCRemotePlugin = environment.loadedInVCRemotePlugin;
        if(this.loadedInVCRemotePlugin) {
            this.remotePluginAuthErrors = [];
            this.authService.isLogInProgress = true;
            // initiate VC session token based login to hcx.
            this.checkAndInitRemotePluginSession((error) => {
                this.remotePluginLoginErrorHandler(error);
            });
        } else {
            this.getEndpointInfo();
        }
    }

    remotePluginLoginErrorHandler(error: any) {
        try {
            if(error) {
                if(typeof error === 'string'){
                    this.remotePluginAuthErrors.push(error || this.translate.instant('auth.unknownError'));
                }else if(error.status === 503) {
                    this.remotePluginAuthErrors.push(this.translate.instant('auth.serverUnavailable'));
                } else if(error.status === 403) {
                    this.remotePluginAuthErrors.push(this.translate.instant('auth.forbiddenRequest'));
                } else if(error.status === 401) {
                    const _errors = error;
                    this.remotePluginAuthErrors.push(_errors.message || this.translate.instant('auth.invalidUsername'));
                } else {
                    const _errors = error;
                    if(Array.isArray(_errors)){
                        _errors.forEach(_error => {
                            this.remotePluginAuthErrors.push(_error.message || this.translate.instant('auth.unknownError'));
                        });
                    } else {
                        this.remotePluginAuthErrors.push(_errors.message || this.translate.instant('auth.unknownError'));
                    }
                }
            }
        }catch(exception) {
            this.remotePluginAuthErrors.push(this.translate.instant('auth.unknownError'));
        }
    }

    ngOnDestroy() {
        if(this.authChangeEventSubscription) {
            this.authChangeEventSubscription.unsubscribe();
            this.authChangeEventSubscription = null;
        }
    }

    getEndpointInfo() {
        this.loading = true;
        this.authService.getEndpointInfo().subscribe(
            (result:any) => {
                this.loading = false;
                this.cloudType = result.data ? result.data.self.cloudType : '';
                if (this.cloudType === this.authConstants.VIO) {
                    this.userNamePlaceholder = 'username@domain';
                }
            },
            error => {
                this.loading = false;
            },
            () => {
                this.loading = false;
            }
        );
    }

    public login() {
        this.loading = true;
        this.wrongUsernameOrPassword = false;
        this.isUnauthorized =  false;
        this.loginErrorMessage = '';

        this.authService.basicUserLogin(this.username, this.password).subscribe(() => {
            //Moved the code to onLoginComplete function
        },
            (error) => {
                console.log('login error: ' + error);
                this.loading = false;
                let errorCode;
                /* If error code is 403 => credentials are correct, but user does not have access to HCX.*/
                if((error.hasOwnProperty('error')) && (error.status.toString() === '401')) {
                    this.wrongUsernameOrPassword = true;
                }
                else {
                  errorCode = error.status;
                }
                if ((!!errorCode && errorCode.toString() === '403')) {
                    this.isUnauthorized = true;
                } else {
                    this.wrongUsernameOrPassword = true;
                }
                this.loginErrorMessage = error.message ? error.message : '';
                console.error(error);
            });
    }

    public onLoginComplete() {
        if (this.authService.isLoggedIn) {
            // reset component state for logout redirect
            this.loading = false;
            this.username = '';
            this.password = '';
            this.wrongUsernameOrPassword = false;
            this.isUnauthorized = false;

            const navigationExtras: NavigationExtras = {
                preserveQueryParams: true,
                preserveFragment: true
            };
            let redirect;
            const roles = (this.authService.loggedInUserInfo && this.authService.loggedInUserInfo.roles) ?
                this.authService.loggedInUserInfo.roles : [];

            if (this.authService.VREALM_TYPE === AUTH_CONSTANTS.VIO) {
                if (_.includes(roles, AUTH_CONSTANTS.OrgAdmin)) {
                    redirect = '/fleet';
                } else if (_.includes(roles, AUTH_CONSTANTS.SysAdmin)) {
                    redirect = '/administration';
                }
            } else if (this.authService.isVCDGVRDepricated) {
                // If this isVCDGVRDepricated, VI Admin should not be allowed to use GVR.

                if (_.includes(roles, 'Organization Administrator')) {
                    // If this is a newVCDGVR, redirect org admin to /disaster-recovery-embed
                    redirect = '/disaster-recovery-embed';
                } else if (_.includes(roles, 'System Administrator')) {
                    // If he is a System Admin, redirect him to administrator page.
                    redirect = '/administration';
                    if (this.authService.delegateAuthToVCD) {
                        // If the authentication was delegated to VCD, then only change hte browser url.
                        window.top.location.href = window.self.location.href;
                    }
                } else {
                    redirect = '/unauthorized';
                }
                this.router.navigate([redirect]);
            } else {
                if (this.authService.isFeatureEnabled('GVR_DASHBOARD_VIEW', 'VISIBLE')) {
                    redirect = this.authService.redirectUrl ? this.authService.redirectUrl : '/dashboard';
                } else if (this.authService.isFeatureEnabled('HCSP_DASHBOARD_VIEW', 'VISIBLE')) {
                    redirect = this.authService.redirectUrl ? this.authService.redirectUrl : '/dashboard-hcsp';
                }
                if (!!redirect) {

                    this.router.navigate([redirect]);
                }
            }
        } else {
            console.log('login: not logged in');
        }
    }

    public logout() {
        this.authService.logout();
    }

    onExitClicked() {
        this.showGovCloudModal = false;
        this.authService.logout();
    }

    onAgreeClicked() {
        this.showGovCloudModal = false;
        this.authService.onGovCloudAgreeClicked();
    }

    checkAndInitRemotePluginSession(errorHandler) {
        if(!this.authService.loggedInUserInfo) {
            this.authService.vcSessionBasedLogin(errorHandler);
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/auth/login.component.ts